import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { UserCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { getRoleHomeRoute } from '@/lib/role-redirect';
import {
  getMe,
  getOrganisations,
  getOrganisationUsers,
  startImpersonation,
} from '@/services/api.client';
import type { OrgUser } from '@/types';

const REASON_CODES = [
  { value: 'support_investigation', label: 'Support Investigation' },
  { value: 'account_recovery', label: 'Account Recovery' },
  { value: 'data_verification', label: 'Data Verification' },
  { value: 'security_audit', label: 'Security Audit' },
  { value: 'managed_estate', label: 'Managed Estate (on behalf of property)' },
] as const;

type ReasonCodeValue = (typeof REASON_CODES)[number]['value'];

const impersonateSchema = z.object({
  reasonCode: z.enum(REASON_CODES.map((r) => r.value) as [ReasonCodeValue, ...ReasonCodeValue[]], {
    message: 'Select a reason code',
  }),
  justification: z.string().min(20, 'Justification must be at least 20 characters'),
});
type ImpersonateForm = z.infer<typeof impersonateSchema>;

export function SuperAdminImpersonationPage() {
  const { setSession } = useImpersonation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null);
  const [actionError, setActionError] = useState('');

  const { data: orgs = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['admin', 'organisations'],
    queryFn: getOrganisations,
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'org-users', selectedOrgId],
    queryFn: () => getOrganisationUsers(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ImpersonateForm>({ resolver: zodResolver(impersonateSchema) });

  const mutation = useMutation({
    mutationFn: (data: ImpersonateForm) => {
      if (!selectedUser) throw new Error('No user selected');
      return startImpersonation({
        targetUserId: selectedUser.id,
        reasonCode: data.reasonCode,
        justification: data.justification,
      });
    },
    onSuccess: async (session) => {
      setSession(session);
      setSelectedUser(null);
      reset();
      // Server swaps the session to the impersonated user; refetch ['me'] so
      // useAuth and all dependent hooks reflect the target user's identity,
      // then navigate to the target's role-home.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      try {
        const me = await getMe();
        void navigate(getRoleHomeRoute(me));
      } catch {
        void navigate('/admin');
      }
    },
    onError: (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as { message?: string };
        const msg = typeof data.message === 'string' ? data.message : 'Unknown error';
        setActionError(msg);
      } else {
        setActionError('Failed to start impersonation session.');
      }
    },
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl text-[var(--text-primary)]">User Impersonation</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          All impersonation sessions are audit-logged and time-limited to 60 minutes.
        </p>
      </div>

      {/* Org selector */}
      <div className="space-y-1">
        <Label htmlFor="org-select">Organisation</Label>
        {orgsLoading ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          <select
            id="org-select"
            aria-label="Organisation"
            value={selectedOrgId}
            onChange={(e) => {
              setSelectedOrgId(e.target.value);
              setSelectedUser(null);
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select an organisation…</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* User list */}
      {selectedOrgId && (
        <div className="rounded-xl border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--bg-surface-raised)]">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Select User</p>
          </div>
          {usersLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <p className="p-4 text-sm text-[var(--text-tertiary)]">
              No users found for this organisation.
            </p>
          ) : (
            <div className="divide-y divide-[var(--bg-surface-raised)]">
              {users.map((u) => {
                const isActive = u.status === 'active';
                return (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                    <UserCheck className="size-4 text-[var(--text-secondary)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)]">{u.fullName}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{u.email}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-xs shrink-0 ${
                        !isActive ? 'border-[var(--status-red)]/40 text-[var(--status-red-text)]' : ''
                      }`}
                    >
                      {u.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {u.role}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedUser(u)}
                      disabled={!isActive}
                      title={!isActive ? `Cannot impersonate — user is ${u.status}` : undefined}
                    >
                      Select
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Impersonation form */}
      {selectedUser && (
        <div className="rounded-xl border border-[var(--status-amber)]/40 bg-[var(--status-amber)]/10 p-4 space-y-4">
          <div>
            <p className="text-sm font-semibold text-[var(--text-primary)]">
              Impersonate: {selectedUser.fullName}
            </p>
            <p className="text-xs text-[var(--text-secondary)]">{selectedUser.email}</p>
          </div>
          <form
            onSubmit={handleSubmit((data) => {
              setActionError('');
              mutation.mutate(data);
            })}
            className="space-y-3"
          >
            <div className="space-y-1">
              <Label htmlFor="reason-code">Reason Code</Label>
              <select
                id="reason-code"
                {...register('reasonCode')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">Select reason…</option>
                {REASON_CODES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
              {errors.reasonCode && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.reasonCode.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="justification">Justification</Label>
              <Input
                id="justification"
                {...register('justification')}
                placeholder="Describe the specific support case requiring impersonation"
              />
              {errors.justification && (
                <p className="text-xs text-[var(--status-red-text)]">{errors.justification.message}</p>
              )}
            </div>
            {actionError && <p className="text-xs text-[var(--status-red-text)]">{actionError}</p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setSelectedUser(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={mutation.isPending}>
                {mutation.isPending ? 'Starting…' : 'Begin Impersonation Session'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
